'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, ChevronDown, X } from 'lucide-react';
import { useCampusSelection } from '@/lib/useCampusSelection';
import { CampusPanelView, FLIP_MS, type CampusPanelProps } from './CampusPanel';

export interface CampusPickerProps extends CampusPanelProps {
  /**
   * Where the open panel renders. In the embed this is a body-level shadow
   * host so the site's fixed header cannot paint over it; the Next preview
   * leaves it unset and the panel renders in document.body.
   */
  portalContainer?: HTMLElement | null;
  /**
   * Open from an element the page already has (the theme's "Locations" nav
   * link) instead of rendering a trigger. Clicks on anything matching the
   * selector open the panel; the element's own navigation is suppressed.
   */
  triggerSelector?: string;
}

const TEAL = '#299c8f';
const CLOSE_MS = 200;

/**
 * The header form: a compact trigger that opens the campus panel as a
 * full-width menu under the header (a full-height sheet on phones), closed
 * until clicked. See CampusPanel for the inline mega-menu form.
 */
export function CampusPicker({
  apiBaseUrl = 'https://api.woodsidebible.org',
  portalContainer,
  triggerSelector,
  ...panelProps
}: CampusPickerProps) {
  const selection = useCampusSelection(apiBaseUrl);
  const { isLoading, selectedCong, selectedSvgUrl, liveByCampus } = selection;
  const selectedLive = selectedCong ? liveByCampus.get(selectedCong.id) : undefined;
  // mounted: the panel is in the DOM; shown: it has finished sliding in.
  // Closing drops `shown` first and unmounts after the transition.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [top, setTop] = useState(0);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const externalTriggerRef = useRef<Element | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const place = useCallback(() => {
    const el = triggerRef.current ?? externalTriggerRef.current;
    const rect = el?.getBoundingClientRect();
    setTop(Math.max(8, (rect?.bottom ?? 0) + 8));
  }, []);

  const open = useCallback(() => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    place();
    setMounted(true);
    // Two frames so the first paint has the closed styles and the transition runs
    requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
  }, [place]);

  const close = useCallback(() => {
    setShown(false);
    closeTimer.current = window.setTimeout(() => {
      setMounted(false);
      closeTimer.current = null;
    }, CLOSE_MS);
  }, []);

  const isOpen = mounted && shown;

  // The page's own trigger, when asked for
  useEffect(() => {
    if (!triggerSelector) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Element | null;
      const hit = target?.closest?.(triggerSelector!);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      externalTriggerRef.current = hit;
      if (mounted) close();
      else open();
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [triggerSelector, mounted, open, close]);

  useEffect(() => {
    if (!triggerSelector) return;
    document
      .querySelectorAll(triggerSelector)
      .forEach((el) => el.setAttribute('aria-expanded', String(mounted)));
  }, [triggerSelector, mounted]);

  // Outside click, Escape, and keep the panel anchored while the window changes
  useEffect(() => {
    if (!mounted) return;
    function onClick(e: MouseEvent) {
      const path = e.composedPath();
      if (panelRef.current && path.includes(panelRef.current)) return;
      if (triggerRef.current && path.includes(triggerRef.current)) return;
      if (externalTriggerRef.current && path.includes(externalTriggerRef.current)) return;
      close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [mounted, close, place]);

  useEffect(
    () => () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    },
    []
  );

  const panel = mounted && (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose your campus"
      className="fixed inset-0 z-[2147483000] font-sans"
    >
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`absolute inset-x-0 top-[6vh] bottom-0 overflow-y-auto rounded-t-2xl bg-white p-4 pt-12 shadow-2xl transition-[opacity,transform] duration-300 ease-out sm:inset-x-4 sm:top-[var(--panel-top)] sm:bottom-auto sm:max-h-[calc(100vh-var(--panel-top)-16px)] sm:rounded-2xl sm:p-6 sm:pt-6 ${
          shown
            ? 'translate-y-0 opacity-100 sm:scale-100'
            : 'translate-y-6 opacity-0 sm:-translate-y-2 sm:scale-[0.98]'
        }`}
        style={{ '--panel-top': `${top}px`, transformOrigin: 'top center' } as React.CSSProperties}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white text-[#1c2b39] shadow-md transition-transform hover:scale-105 active:scale-95"
        >
          <X className="h-4 w-4" />
        </button>
        {/* A pick slides the chosen card to the first slot; let that play before closing */}
        <CampusPanelView
          selection={selection}
          onPicked={() => window.setTimeout(close, FLIP_MS + 80)}
          {...panelProps}
          onNavigate={close}
        />
      </div>
    </div>
  );

  const portaled = panel && (portalContainer ? createPortal(panel, portalContainer) : panel);

  if (triggerSelector) return <>{portaled}</>;

  return (
    <div className="relative inline-block font-sans">
      <button
        ref={triggerRef}
        onClick={() => (mounted ? close() : open())}
        className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-black/5"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="relative flex items-center">
          {selectedSvgUrl ? (
            <img src={selectedSvgUrl} alt="" className="h-6 w-6 object-contain" />
          ) : (
            <MapPin className="h-5 w-5" style={isOpen ? { color: TEAL } : undefined} />
          )}
          {selectedLive?.isLive && (
            <span
              className="absolute -top-1 -right-1 flex h-2.5 w-2.5 items-center justify-center"
              title="Your campus is live now"
              aria-label="Your campus is live now"
              role="img"
            >
              <span className="absolute inline-flex h-2 w-2 rounded-full bg-red-500 opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600 ring-2 ring-white" />
            </span>
          )}
        </span>
        {isLoading ? (
          <span className="max-w-[140px] truncate">Loading...</span>
        ) : selectedCong ? (
          <>
            {/* The two-letter code where the header is tight, the name where it is not */}
            <span className="max-w-[140px] truncate max-sm:hidden">{selectedCong.name}</span>
            <span className="sm:hidden">
              {selectedCong.short_name ?? selectedCong.display_name}
            </span>
          </>
        ) : (
          <span className="max-w-[140px] truncate">Choose campus</span>
        )}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {portaled}
    </div>
  );
}
